/**
 * Scroll carousels for the homepage sections.
 *
 * Shared by the collection carousel and the reviews carousel. A section opts in
 * with data-home-carousel, a scrolling element with data-home-carousel-track, and
 * optional data-home-carousel-prev / -next controls.
 *
 * The track is a native scroller, so touch swipe, trackpads, keyboard scrolling and
 * scroll snapping are the browser's job. This only adds the paging controls, their
 * disabled states, and autoplay.
 */
(() => {
  'use strict';

  const EDGE_TOLERANCE = 2;

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const init = (section) => {
    if (section.dataset.carouselInitialized === 'true') return;

    const track = section.querySelector('[data-home-carousel-track]');
    if (!track) return;

    section.dataset.carouselInitialized = 'true';

    const prev = section.querySelector('[data-home-carousel-prev]');
    const next = section.querySelector('[data-home-carousel-next]');
    const viewport = section.querySelector('[data-carousel-viewport]');

    const canScroll = () => track.scrollWidth - track.clientWidth > EDGE_TOLERANCE;
    const atStart = () => track.scrollLeft <= EDGE_TOLERANCE;
    const atEnd = () =>
      track.scrollLeft + track.clientWidth >= track.scrollWidth - EDGE_TOLERANCE;

    // A card's width plus the gap, so a click lands on the next snap point rather
    // than part way through a card.
    const step = () => {
      const card = track.querySelector(':scope > *');
      if (!card) return track.clientWidth;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return card.getBoundingClientRect().width + gap;
    };

    const syncControls = () => {
      if (viewport) viewport.dataset.carouselOverflow = String(canScroll());
      if (prev) prev.disabled = !canScroll() || atStart();
      // The last card stays reachable while autoplay can still wrap around.
      if (next) next.disabled = !canScroll() || atEnd();
    };

    // Reduced motion takes the animation away, not the paging itself.
    const behavior = () => (prefersReducedMotion() ? 'auto' : 'smooth');

    const move = (direction) => {
      if (direction > 0 && atEnd()) {
        track.scrollTo({ left: 0, behavior: behavior() });
        return;
      }
      track.scrollBy({ left: step() * direction, behavior: behavior() });
    };

    if (prev) prev.addEventListener('click', () => move(-1));
    if (next) next.addEventListener('click', () => move(1));

    track.addEventListener('scroll', syncControls, { passive: true });
    window.addEventListener('resize', syncControls);
    syncControls();

    // Autoplay: paused on hover, while the section holds focus, and when the tab is
    // in the background.
    if (section.dataset.autoplay !== 'true') return;

    const speed = Number(section.dataset.autoplaySpeed) || 5000;

    // The timer runs continuously and each tick decides whether to advance. A
    // paused-flag approach can get stuck: pointerleave does not always fire after a
    // programmatic scroll, and autoplay then never resumes. Asking :hover at tick
    // time cannot wedge, and it stays true while the cursor is anywhere in the
    // section, however the cards move underneath it.
    const shouldHold = () =>
      document.hidden ||
      section.matches(':hover') ||
      section.contains(document.activeElement);

    window.setInterval(() => {
      if (shouldHold() || !canScroll()) return;
      move(1);
    }, speed);
  };

  const scan = (root) => {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-home-carousel]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  } else {
    scan(document);
  }

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
